import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

const TRACK_ENABLE_SMS_ON_SAVE_PROMPT_DESCRIPTION =
    'User clicks on the "Text Me" CTA after being prompted to turn on SMS notifications after saving an item';

export enum TrackEnableSmsOnSavePromptActionTaken {
    Accepted = 'Accepted',
    Closed = 'Closed',
    Error = 'Error',
    Rejected = 'Rejected',
    Viewed = 'Viewed',
}

type TrackEnableSmsOnSavePromptPayload = TrackingPayload & {
    actionTaken: TrackEnableSmsOnSavePromptActionTaken;
    description: boolean;
};

const trackEnableSmsOnSavePrompt = (payload: TrackingPayload) => {
    track(TrackingEvents.Notifications.EnableSmsOnSavePrompt, payload);
};

export const useTrackEnableSmsOnSavePrompt = () => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    return useCallback(
        ({ actionTaken }: Omit<TrackEnableSmsOnSavePromptPayload, 'pageName' | 'url'>) => {
            trackEnableSmsOnSavePrompt({
                actionTaken,
                description: TRACK_ENABLE_SMS_ON_SAVE_PROMPT_DESCRIPTION,
                pageName: getAmplitudePageNameFromPath(pathname),
                url,
            });
        },
        [pathname, url]
    );
};
